import { QueryClient, QueryFunction } from "@tanstack/react-query";

/**
 * Check if a response is OK, throw an error with details if not
 */
async function throwIfResNotOk(res: Response): Promise<void> {
  if (!res.ok) {
    const contentType = res.headers.get('content-type');

    if (contentType && contentType.includes('text/html')) {
      if (res.status === 401) {
        throw new Error('Authentication required. Please log in and try again.');
      } else {
        throw new Error(`Server error (${res.status}): Unexpected HTML response.`);
      }
    }

    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * API request function
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";

/**
 * Query function for React Query
 */
export const getQueryFn = <T>({ on401 }: { on401: UnauthorizedBehavior }): QueryFunction<T> => {
  return async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
    });

    if (on401 === "returnNull" && res.status === 401) {
      return null as T;
    }

    await throwIfResNotOk(res);
    return res.json();
  };
};

/**
 * React Query client configuration
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
});
